import React from 'react';
import styled from 'styled-components';
import gql from 'graphql-tag';
import get from 'lodash.get';
import { History } from 'history';
import { useQuery } from 'react-apollo';
import { useSelector } from 'react-redux';
import { utils, TimeSlot, PartnerClinicDetails } from 'kb-shared';

import UpcomingAppointment from '../../../components/UpcomingAppointment';
import IntakeFormCard from '../../../components/IntakeFormCard';
import { MembershipConfirmationCard } from '../../../components/ConfirmationCards';
import { ReduxState } from '../../../redux';
import { PartnerLocationConfirmationCard } from './PartnerLocation';
import { PurchasedProduct } from '../utils/BookingStateManager';

const {
  isAssessment,
  isGYNAppointment,
  isVirtualConsultation,
  isPulseUltrasoundBloodwork,
  intakeFormId
} = utils;

const CardContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  margin: 10px;
  box-shadow: -4px 4px 0 0 ${({ theme }) => theme.colors.KBNight};
  border: solid 2px ${({ theme }) => theme.colors.KBNight};

  @media ${({ theme }) => theme.queries.desktop} {
    width: 359px;
  }

  @media ${({ theme }) => theme.queries.tablet} {
    width: 276px;
  }

  @media ${({ theme }) => theme.queries.mobile} {
    width: 288px;
  }
`;

const Container = styled.div`
  display: flex;
  justify-content: center;

  @media ${({ theme }) => theme.queries.mobile} {
    flex-flow: row wrap;
  }
`;

type Props = {
  purchase: PurchasedProduct;
  onReturnToBookAppointment: () => void;
  timeSlot: TimeSlot | null;
  history: History;
  partnerClinicDetails?: PartnerClinicDetails | null;
  appointmentName?: string | null;
};

type PatientGender = 'male' | 'female' | undefined;

export const INTAKE_FORM = gql`
  query intakeForm($formId: Int!) {
    formCompletion(formId: $formId) {
      percentageComplete
    }
  }
`;

const PurchaseConfirmation = (props: Props) => {
  const { purchase, timeSlot, history, partnerClinicDetails, appointmentName } = props;
  const gender = useSelector<ReduxState>(state => state.patient.patient.gender) as PatientGender;
  const { data } = useQuery(INTAKE_FORM, {
    variables: {
      formId: intakeFormId(process.env.REACT_APP_ENV, gender)
    }
  });
  const intakeFormCompletionPercent = get(data, 'formCompletion.percentageComplete', null);

  function goToBook() {
    props.onReturnToBookAppointment();
  }

  if (partnerClinicDetails) {
    return (
      <Container id="google-tag-manager-confirmation">
        {partnerClinicDetails && (
          <PartnerLocationConfirmationCard
            partnerClinicDetails={partnerClinicDetails}
            appointmentName={appointmentName}
          />
        )}
      </Container>
    );
  }

  if (purchase.type === 'appointment') {
    // @ts-ignore
    const appointment = purchase.data.id ? purchase.data : purchase.data.appointment;
    const categoryId =
      appointment.appointmentType.category && appointment.appointmentType.category.id;
    const isAssessmentAppointment = isAssessment(appointment, process.env.REACT_APP_ENV);
    const isGynAppointment = isGYNAppointment(parseInt(categoryId), process.env.REACT_APP_ENV);
    const isVirtualConsultationAppointment = isVirtualConsultation(
      appointment,
      process.env.REACT_APP_ENV
    );

    return (
      <Container id="google-tag-manager-confirmation">
        {!partnerClinicDetails && (
          <CardContainer>
            <UpcomingAppointment
              appointment={appointment}
              timeSlot={timeSlot}
              type={appointment.appointmentType}
              isConfirmation={true}
              isPartOfPackage={appointment.patientPackageId !== undefined}
              onReschedule={() => {
                props.history.push('/booking-quiz');
                window.location.reload();
              }}
            />
          </CardContainer>
        )}
        {(isAssessmentAppointment ||
          isGynAppointment ||
          isVirtualConsultationAppointment ||
          (isPulseUltrasoundBloodwork(appointment, process.env.REACT_APP_ENV) &&
            intakeFormCompletionPercent < 100)) && (
          <CardContainer>
            <IntakeFormCard history={history} isGyn={isGynAppointment} confirmation />
          </CardContainer>
        )}
      </Container>
    );
  } else if (purchase.type === 'membership' || purchase.type === 'enterprise_membership') {
    return <MembershipConfirmationCard onClick={goToBook} id="google-tag-manager-confirmation" />;
  }
  return null;
};

export default PurchaseConfirmation;
